import { useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { APP_NAME, APP_TAGLINE, LOGO_SRC } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { BlueprintBackdrop } from "@/components/blueprint-backdrop";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            // Server always returns 200 (no account enumeration); show the same
            // neutral confirmation regardless of whether the email matched.
            await api.post("/api/auth/forgot-password", { email });
        } catch {
            // Network error only — still show the neutral confirmation.
        }
        setDone(true);
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <BlueprintBackdrop verticalAnchor="viewport" />
            <div className="relative w-full max-w-sm">
                <Card className="shadow-[var(--shadow-elevated)] border-border/40 bg-card">
                    <CardHeader className="text-center pb-3 pt-8">
                        <h1 className="sr-only">{APP_NAME} — Reset password</h1>
                        <div className="mx-auto mb-5 flex justify-center">
                            <img
                                src={LOGO_SRC}
                                alt=""
                                className="h-[7.5rem] w-auto max-w-full object-contain object-center"
                                width={320}
                                height={120}
                            />
                        </div>
                        <p className="text-sm font-medium text-foreground">Reset your password</p>
                    </CardHeader>
                    <CardContent>
                        {done ? (
                            <div className="space-y-4">
                                <p className="text-sm text-foreground/80">
                                    If an account exists for that email, we&apos;ve sent a link to reset your password.
                                    Check your inbox (and spam folder).
                                </p>
                                <Button asChild variant="outline" className="w-full">
                                    <a href="/login">Back to sign in</a>
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <p className="text-sm text-foreground/80">
                                    Enter your email and we&apos;ll send you a link to set a new password.
                                </p>
                                <div className="space-y-1.5">
                                    <label htmlFor="email" className="text-sm font-medium">
                                        Email
                                    </label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="you@company.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoFocus
                                        className="h-10"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full h-10 font-semibold shadow-md bg-primary hover:bg-primary/90 text-primary-foreground border-0"
                                    disabled={submitting}
                                >
                                    {submitting ? "Sending…" : "Send reset link"}
                                </Button>
                                <div className="text-center">
                                    <a href="/login" className="text-sm text-primary hover:underline">
                                        Back to sign in
                                    </a>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>
                <p className="text-center text-xs text-shell-foreground/80 mt-6 max-w-xs mx-auto">{APP_TAGLINE}</p>
            </div>
        </div>
    );
}
